import { FaWhatsapp } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";

const SPRINTCORE_COLORS = [
  "#FFD76A",
  "#F6C453",
  "#E5A900",
  "#D08E00",
  "#B77900",
  "#FFFFFF",
];

interface LightningLine {
  x: number;
  y: number;
  baseY: number;
  length: number;
  speed: number;
  opacity: number;
  thickness: number;
  color: string;
  age: number;
  segments: number;
  amplitude: number;
}

function LightningCanvas({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number | null>(null);
  const linesRef = useRef<LightningLine[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    function createLightning(): LightningLine {
      const color =
        SPRINTCORE_COLORS[
          Math.floor(Math.random() * SPRINTCORE_COLORS.length)
        ];

      const y = 8 + Math.random() * (H - 16);

      return {
        x: -(30 + Math.random() * 30),
        y,
        baseY: y,
        length: 30 + Math.random() * 50,
        speed: 3 + Math.random() * 4,
        opacity: 0.45 + Math.random() * 0.45,
        thickness: 0.8 + Math.random() * 1.3,
        color,
        age: 0,
        segments: 5 + Math.floor(Math.random() * 5),
        amplitude: 3 + Math.random() * 6,
      };
    }

    function spawnBurst() {
      for (let i = 0; i < 8; i++) {
        const lightning = createLightning();

        lightning.x =
          -lightning.length -
          Math.random() * 50;

        linesRef.current.push(lightning);
      }
    }

    function drawLightning(
      line: LightningLine,
      strokeColor: string,
      lineWidth: number,
    ) {
      const segmentLength = line.length / line.segments;

      ctx.beginPath();
      ctx.moveTo(line.x, line.baseY);

      for (let i = 1; i <= line.segments; i++) {
        const x =
          line.x +
          segmentLength * i;

        const isLast =
          i === line.segments;

        const randomOffset =
          isLast
            ? 0
            : (Math.random() - 0.5) *
              line.amplitude *
              2;

        ctx.lineTo(
          x,
          line.baseY + randomOffset,
        );
      }

      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }

    let frame = 0;

    function draw() {
      ctx.clearRect(0, 0, W, H);

      if (frame % 5 === 0) {
        linesRef.current.push(createLightning());
      }

      linesRef.current =
        linesRef.current.filter(
          (line) =>
            line.x <
            W + line.length,
        );

      for (const line of linesRef.current) {
        line.x += line.speed;
        line.age += 1;

        const progress =
          line.x / W;

        let fade = 1;

        if (progress < 0.12) {
          fade = Math.max(
            0,
            progress / 0.12,
          );
        }

        if (progress > 0.82) {
          fade = Math.max(
            0,
            1 -
              (progress - 0.82) /
                0.18,
          );
        }

        ctx.save();

        ctx.globalAlpha =
          line.opacity * fade;

        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        ctx.shadowColor =
          line.color;

        ctx.shadowBlur = 7;

        drawLightning(
          line,
          line.color,
          line.thickness,
        );

        ctx.globalAlpha =
          Math.min(
            1,
            line.opacity *
              fade *
              0.8,
          );

        ctx.shadowColor =
          "#FFFFFF";

        ctx.shadowBlur = 3;

        drawLightning(
          line,
          "#FFFFFF",
          Math.max(
            0.35,
            line.thickness * 0.35,
          ),
        );

        ctx.restore();
      }

      frame++;

      animRef.current =
        requestAnimationFrame(draw);
    }

    if (active) {
      spawnBurst();
      draw();
    } else {
      linesRef.current = [];

      ctx.clearRect(
        0,
        0,
        W,
        H,
      );
    }

    return () => {
      if (animRef.current) {
        cancelAnimationFrame(
          animRef.current,
        );
      }
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={60}
      className="
        pointer-events-none
        absolute
        inset-0
        h-full
        w-full
      "
    />
  );
}

export default function WhatsAppFloatButton() {
  const phone = "593996337323";

  const message = encodeURIComponent(
    "Hola, quiero recibir más información sobre los servicios de SprintCore.",
  );

  const [hovered, setHovered] =
    useState(false);

  const handleClick = () => {
    window.open(
      `https://wa.me/${phone}?text=${message}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
      <style>{`

        /* =========================================
           ANIMACIÓN DEL BRILLO EXTERIOR
        ========================================= */

        @keyframes sprint-wa-glow {
          0%, 100% {
            box-shadow:
              0 0 12px rgba(246, 196, 83, 0.22),
              0 6px 26px rgba(0, 0, 0, 0.38);
          }

          50% {
            box-shadow:
              0 0 30px rgba(246, 196, 83, 0.48),
              0 10px 38px rgba(0, 0, 0, 0.45);
          }
        }

        /* =========================================
           ICONO
        ========================================= */

        @keyframes sprint-wa-icon {
          0% {
            transform:
              scale(1)
              rotate(0deg);
          }

          30% {
            transform:
              scale(1.15)
              rotate(-10deg);
          }

          65% {
            transform:
              scale(1.20)
              rotate(8deg);
          }

          100% {
            transform:
              scale(1.12)
              rotate(0deg);
          }
        }

        /* =========================================
           BRILLO DE MONEDA
        ========================================= */

        @keyframes coin-shimmer {
          0% {
            transform:
              translateX(-180%)
              rotate(18deg);
          }

          100% {
            transform:
              translateX(260%)
              rotate(18deg);
          }
        }

        /* =========================================
           BOTÓN PRINCIPAL
        ========================================= */

        .sprint-wa-button {
          width: 58px;

          transition:
            width 450ms cubic-bezier(0.22, 1, 0.36, 1),
            padding 450ms cubic-bezier(0.22, 1, 0.36, 1),
            gap 450ms cubic-bezier(0.22, 1, 0.36, 1),
            transform 350ms cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 400ms ease,
            border-color 400ms ease;
        }

        .sprint-wa-button:hover {
          width: 205px;
          transform: scale(1.04);
        }

        .sprint-wa-hovered {
          animation:
            sprint-wa-glow
            1.6s
            ease-in-out
            infinite;
        }

        .sprint-wa-icon-hovered {
          animation:
            sprint-wa-icon
            550ms
            ease
            forwards;
        }

        /* =========================================
           TEXTO
        ========================================= */

        .sprint-wa-text {
          max-width: 0;
          opacity: 0;
          transform: translateX(-8px);
          overflow: hidden;

          transition:
            max-width 450ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 300ms ease,
            transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .sprint-wa-button:hover .sprint-wa-text {
          max-width: 130px;
          opacity: 1;
          transform: translateX(0);
        }

        /* =========================================
           BRILLO QUE CRUZA LA MONEDA
        ========================================= */

        .gold-coin-shimmer {
          position: absolute;

          width: 17px;
          height: 130%;

          top: -15%;
          left: 0;

          opacity: 0.48;

          filter: blur(3px);

          background:
            linear-gradient(
              90deg,
              transparent,
              rgba(255,255,255,0.75),
              rgba(255,245,190,0.95),
              transparent
            );

          transform:
            translateX(-180%)
            rotate(18deg);

          animation:
            coin-shimmer
            4.8s
            cubic-bezier(0.4, 0, 0.2, 1)
            infinite;

          pointer-events: none;
        }

        @media (max-width: 767px) {
          .sprint-wa-button:hover {
            width: 58px;
            transform: scale(1.05);
          }

          .sprint-wa-text {
            display: none;
          }
        }
      `}</style>

      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() =>
          setHovered(true)
        }
        onMouseLeave={() =>
          setHovered(false)
        }
        className={`
          sprint-wa-button fixed bottom-6 right-6 z-[100] flex h-[58px] items-center justify-center overflow-hidden rounded-full border
          ${
            hovered
              ? "sprint-wa-hovered border-[#F6C453]/60"
              : "border-[#8A5D00]"
          }
        `}
        style={{
          gap: hovered
            ? "10px"
            : "0px",

          paddingLeft:
            hovered
              ? "18px"
              : "0px",

          paddingRight:
            hovered
              ? "18px"
              : "0px",

          /*
            ESTADO HOVER:
            más oscuro para que los rayos
            tengan contraste.
          */
          background: hovered
            ? `
              linear-gradient(
                135deg,
                #211503 0%,
                #4D3100 28%,
                #8D5A00 58%,
                #D08E00 82%,
                #F6C453 100%
              )
            `

            /*
              ESTADO NORMAL:
              moneda de oro.
            */
            : `
              radial-gradient(
                circle at 32% 22%,
                #FFF4B3 0%,
                #FFD86A 13%,
                #F6C453 28%,
                #D99B12 48%,
                #B97600 67%,
                #8E5800 82%,
                #5A3500 100%
              )
            `,

          /*
            La combinación de sombras
            crea el relieve de moneda.
          */
          boxShadow: hovered
            ? undefined
            : `
              inset 0 2px 2px rgba(255,255,255,0.65),
              inset 0 -4px 6px rgba(78,41,0,0.65),
              inset 3px 0 5px rgba(255,225,120,0.22),
              0 0 0 1px rgba(255,218,91,0.45),
              0 0 0 3px rgba(113,68,0,0.26),
              0 8px 20px rgba(0,0,0,0.38),
              0 3px 6px rgba(0,0,0,0.28)
            `,
        }}
        aria-label="Contactar a SprintCore por WhatsApp"
      >

        <LightningCanvas
          active={hovered}
        />

        {!hovered && (
          <div
            className="pointer-events-none absolute inset-[3px] rounded-full border border-[#FFE28A]/70 shadow-[inset_0_0_0_1px_rgba(116,68,0,0.55)] z-[2]"
          />
        )}

        {!hovered && (
          <div
            className="pointer-events-none absolute inset-[6px] rounded-full border border-white/15 z-[2] "
          />
        )}

        {!hovered && (
          <div
            className="pointer-events-none absolute left-[13%] right-[13%] top-[7%] h-[36%] rounded-full bg-gradient-to-b from-white/45 via-white/10 to-transparent blur-[1px] z-[3]"
          />
        )}

        {!hovered && (
          <div className="gold-coin-shimmer z-[4]" />
        )}

        {!hovered && (
          <div
            className="
              pointer-events-none absolute bottom-[5px] left-[16%] right-[16%] h-[15%] rounded-full bg-black/20 blur-[3px] z-[2]"
          />
        )}

        {hovered && (
          <div
            className="pointer-events-none absolute inset-[1px] rounded-full bg-gradient-to-b from-white/15 via-transparent to-black/10 z-[2] "
          />
        )}

        <FaWhatsapp
          size={24}
          className={`relative z-10 shrink-0 transition-colors duration-300
            ${
              hovered
                ? "sprint-wa-icon-hovered text-white"
                : "text-[#4C2E00] drop-shadow-[0_1px_1px_rgba(255,242,174,0.75)]"
            }
          `}
        />
        <span
          className="sprint-wa-text relative z-10 hidden whitespace-nowrap text-[13px] font-semibold tracking-wide text-white md:inline-block"
        >
          Contáctanos
        </span>
      </button>
    </>
  );
}